import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, FileText, Route as RouteIcon, ScanSearch, Sparkles } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import TrustSection from "@/components/TrustSection";
import SEO, { BRAND_LOGO_URL, CANONICAL_ORIGIN } from "@/components/SEO";
import SolutionDiscovery from "@/components/SolutionDiscovery";

const HELP_CARDS = [
  {
    icon: ScanSearch,
    title: "Sorunu anlar",
    text: "Sorununuzu ve ilgili kurumu belirleyerek doğru çözüm akışını başlatır.",
  },
  {
    icon: RouteIcon,
    title: "Doğru yolu seçer",
    text: "Mevcut kurallara ve pratik iş akışına göre size uygun başvuru adımlarını çıkarır.",
  },
  {
    icon: FileText,
    title: "Hazır metni oluşturur",
    text: "Kurumunuza gönderebileceğiniz metni, gerekli belge listesini ve takip planını hazırlar.",
  },
];

export default function Home() {
  const navigate = useNavigate();
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Abonelik İptal",
      url: CANONICAL_ORIGIN,
      logo: BRAND_LOGO_URL,
      description: "Abonelik İptal, Türkiye'de şirketler ve kamu kurumlarıyla yaşanan sorunlar için doğru çözüm yolunu bulmaya yardımcı olur.",
      sameAs: [],
    },
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "Abonelik İptal",
      url: CANONICAL_ORIGIN,
      logo: BRAND_LOGO_URL,
      inLanguage: "tr-TR",
    },
    {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: "Abonelik İptal",
      url: CANONICAL_ORIGIN,
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      offers: { "@type": "Offer", price: "0", priceCurrency: "TRY" },
      inLanguage: "tr-TR",
    },
  ];

  return (
    <div>
      <SEO
        title="Abonelik İptal | Sorununuz İçin Doğru Çözüm Yolunu Bulun"
        description="Abonelik iptalinden para iadesine, şikâyetten resmî başvuruya kadar doğru çözüm yolunu Abonelik İptal ile keşfedin."
        path="/"
        jsonLd={jsonLd}
      />
      <Header />
      <SolutionDiscovery />

      <section data-testid="help-cards" className="fs-wide-container mt-20 md:mt-28">
        <div className="max-w-2xl mb-8 md:mb-10">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--feshet-teal)] mb-3">Abonelik İptal Asistanı</div>
          <h2 className="text-3xl md:text-4xl font-bold text-[var(--feshet-text)] tracking-tight">Seçtiğiniz çözümü dosyaya dönüştürün</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-5 md:gap-6">
          {HELP_CARDS.map((card) => {
            const Icon = card.icon;
            return (
              <div key={card.title} className="fs-card p-6 md:p-7">
                <div className="w-12 h-12 rounded-2xl bg-[var(--feshet-petrol)] text-white flex items-center justify-center">
                  <Icon size={22} strokeWidth={2.25} />
                </div>
                <h3 className="mt-5 text-xl font-semibold text-[var(--feshet-text)]">{card.title}</h3>
                <p className="mt-2 text-sm text-[var(--feshet-muted)] leading-relaxed">{card.text}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section data-testid="howitworks-strip" className="fs-wide-container mt-20 md:mt-28">
        <div className="rounded-[2rem] bg-[var(--feshet-petrol)] text-white p-8 md:p-14 relative overflow-hidden">
          <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-[var(--feshet-turquoise)]/15 blur-2xl" />
          <div className="relative max-w-2xl">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--feshet-turquoise)] mb-3">Nasıl çalışır?</div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">Kısa seçimlerle başvurunuz hazır.</h2>
            <p className="mt-3 text-white/70">Sorununuzu, kurumu ve yaşadığınız durumu seçin; Abonelik İptal size başvuru yolunu, hazır metni ve takip planını sunsun.</p>
          </div>
          <div className="relative mt-9 flex flex-wrap gap-3">
            <button data-testid="how-start-btn" onClick={() => navigate("/dosya-baslat")} className="fs-btn-primary inline-flex items-center gap-2">
              Başvuru Hazırla <ArrowRight size={18} />
            </button>
            <Link to="/nasil-calisir" className="rounded-full border border-white/30 px-6 py-3.5 text-sm font-bold hover:bg-white/10 transition-colors">Nasıl çalıştığını görün</Link>
          </div>
        </div>
      </section>

      <TrustSection />

      <section data-testid="popular-topics" className="fs-wide-container mt-20 md:mt-28">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[var(--feshet-teal)]">
          <Sparkles size={14} /> Rehberler
        </div>
        <h2 className="mt-3 text-3xl md:text-4xl font-bold text-[var(--feshet-text)] tracking-tight max-w-xl">Popüler başvuru konuları</h2>
        <div className="mt-8 flex flex-wrap gap-3">
          {[
            "vodafone-net-iptal",
            "superonline-iptal",
            "turk-telekom-internet-iptal",
            "dsmart-iptal",
            "digiturk-iptal",
            "trendyol-iade-itiraz",
            "hepsiburada-iade-itiraz",
          ].map((slug) => (
            <Link key={slug} to={`/${slug}`} data-testid={`chip-${slug}`} className="rounded-full bg-white border border-slate-200 px-5 py-2.5 text-sm font-semibold text-[var(--feshet-text)] hover:border-[var(--feshet-teal)] transition-colors">
              {slug.replace(/-/g, " ")}
            </Link>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
